/**
 * 蜜糖黄 · 阳光黄页 独立单页（帮助/关于/协议等）
 *
 * getSinglePage(slug) 动态加载；内容 HTML 白卡内渲染；同站其他页面列表附尾。
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getSinglePage, type SinglePage as SinglePageType } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import HoneyNavbar from "@/templates/honey/components/Navbar";
import HoneyFooter from "@/templates/honey/components/Footer";

function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function HoneySinglePage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { siteName } = useSite();
  const [page, setPage] = useState<SinglePageType | null>(null);
  const [otherPages, setOtherPages] = useState<SinglePageType[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let ignore = false;
    setLoading(true);
    setNotFound(false);
    setPage(null);

    getSinglePage(slug)
      .then((res) => {
        if (ignore) return;
        const data: any = res;
        if (!data) {
          setNotFound(true);
          return;
        }
        setPage(data as SinglePageType);
        if (Array.isArray(data.list) && data.list.length > 0) {
          setOtherPages(data.list.filter((p: SinglePageType) => p.slug !== slug).slice(0, 6));
        } else {
          setOtherPages([]);
        }
      })
      .catch(() => {
        if (!ignore) setNotFound(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#FFF9EC]">
      <HoneyNavbar />
      <div className="lg:pl-60">
        {/* 页头 */}
        <section className="bg-[#23201A] relative overflow-hidden">
          <div className="pointer-events-none absolute -top-14 right-16 w-48 h-48 rounded-full border-[14px] border-[#F0A500]/10" />
          <div className="relative max-w-4xl mx-auto px-4 lg:px-8 py-10">
            <nav className="flex items-center gap-1.5 text-xs text-[#8B8577]">
              <Link to="/" className="hover:text-[#F0A500] transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-[#D8D1BF]">{page?.title || "页面加载中…"}</span>
            </nav>
            <div className="mt-5 flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-[#F0A500] text-[#23201A] flex items-center justify-center shrink-0">
                <i className="ri-file-text-line text-xl" />
              </span>
              <h1 className="text-2xl sm:text-3xl font-black text-[#FFF9EC] tracking-tight">{page?.title || "页面"}</h1>
            </div>
          </div>
        </section>

        {/* 正文 */}
        <section className="max-w-4xl mx-auto px-4 lg:px-8 py-8 pb-14">
          {loading ? (
            <div className="bg-white border border-[#EAE1CB] rounded-2xl p-6 sm:p-8 animate-pulse">
              <div className="h-4 w-full rounded bg-[#F3ECDC]" />
              <div className="mt-3 h-4 w-5/6 rounded bg-[#F3ECDC]" />
              <div className="mt-3 h-4 w-2/3 rounded bg-[#F3ECDC]" />
              <div className="mt-6 h-56 rounded-xl bg-[#F3ECDC]" />
            </div>
          ) : notFound || !page ? (
            <div className="bg-white border border-[#EAE1CB] rounded-2xl py-20 text-center">
              <i className="ri-file-unknow-line text-6xl text-[#E4D9BB]" />
              <h2 className="mt-4 text-xl font-black text-[#23201A]">页面不存在或已被移除</h2>
              <p className="mt-1.5 text-sm text-[#8B8577]">您访问的页面可能已被删除或地址有误</p>
              <button
                onClick={() => navigate("/")}
                className="mt-6 px-6 py-2.5 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-black hover:bg-[#FFB520] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1 align-middle" />返回首页
              </button>
            </div>
          ) : (
            <>
              {/* 内容卡 */}
              <article className="bg-white border border-[#EAE1CB] rounded-2xl overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-[#F0A500] via-[#FFBE30] to-[#F0A500]" />
                <div className="p-6 sm:p-9">
                  <div className="flex flex-wrap items-center justify-between gap-2 pb-5 border-b border-dashed border-[#EAE1CB]">
                    <h2 className="text-lg font-black text-[#23201A]">{page.title}</h2>
                    {page.created_at && (
                      <span className="text-xs text-[#8B8577]">
                        <i className="ri-calendar-line mr-1 align-middle" />
                        更新于 {formatDate(page.created_at)}
                      </span>
                    )}
                  </div>
                  <div
                    className="mt-6 prose prose-sm max-w-none text-[#4A453B] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: page.content }}
                  />
                </div>
              </article>

              {/* 其他页面 */}
              {otherPages.length > 0 && (
                <div className="mt-8 bg-white border border-[#EAE1CB] rounded-2xl p-6">
                  <h3 className="flex items-center gap-2 text-base font-black text-[#23201A]">
                    <span className="w-1.5 h-5 bg-[#F0A500] rounded-full inline-block" />
                    {siteName || ""} 更多服务
                  </h3>
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {otherPages.map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={`/page/${p.slug}`}
                          className="flex items-center gap-2 px-3.5 py-3 rounded-xl bg-[#FFF9EC] border border-[#EAE1CB] text-sm text-[#4A453B] hover:border-[#F0A500] hover:text-[#B87A00] hover:shadow-sm transition-all"
                        >
                          <i className="ri-file-text-line text-[#B87A00] shrink-0" />
                          <span className="truncate font-semibold">{p.title}</span>
                          <i className="ri-arrow-right-s-line ml-auto text-[#C9BC95]" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>

        <HoneyFooter />
      </div>
    </div>
  );
}
